import { articles } from '@/mocks/articles';
import { articleContent } from '@/mocks/articleContent';

export const SITE_NAME = 'AllChinaBuy Spreadsheet';

// Real site origin is provided by the platform via the VITE_SITE_URL environment variable.
export const SITE_URL = (import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');

// Authoritative logo/favicon image provided through the SEO embed data.
export const SITE_LOGO =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

// Home hero background image already present in the project.
export const HOME_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=An%20overhead%20flat%20lay%20of%20neatly%20arranged%20pastel%20streetwear%20clothing%20with%20sneakers%20t-shirts%20hoodies%20and%20caps%20on%20a%20light%20beige%20surface%2C%20soft%20natural%20daylight%2C%20clean%20editorial%20fashion%20photography%2C%20airy%20minimal%20aesthetic%2C%20warm%20neutral%20tones&width=1920&height=1080&seq=hero-main&orientation=landscape&nocache=true';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType: 'website' | 'article';
  ogImage: string;
  indexable: boolean;
}

const HOME_TITLE = 'AllChinaBuy Spreadsheet 2026 — Products, Sellers, QC & Shipping';
const HOME_DESCRIPTION =
  'Explore the AllChinaBuy spreadsheet, product resources, seller guides, QC information, shipping guides and practical buying tips in one place. Research smarter before you order.';
const HOME_KEYWORDS =
  'AllChinaBuy, spreadsheet, buying guide, shipping, QC, coupon, discount, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy discount code';
const FALLBACK_KEYWORDS =
  'AllChinaBuy, spreadsheet, buying guide, shipping, QC, coupon, discount';

const pathToSlug: Record<string, string> = {
  'allchinabuy-spreadsheet': 'allchinabuy-spreadsheet-2026-guide',
  'allchinabuy-review': 'allchinabuy-review-2026',
  'allchinabuy-shipping-cost': 'allchinabuy-shipping-cost-guide',
  'allchinabuy-coupon': 'allchinabuy-coupon-discount-guide',
  'allchinabuy-warehouse': 'allchinabuy-warehouse-guide',
  'allchinabuy-qc-guide': 'allchinabuy-qc-guide',
  'allchinabuy-beginner-guide': 'allchinabuy-beginner-guide',
  'is-allchinabuy-legit': 'is-allchinabuy-legit',
};

const langLocales: Record<string, string> = {
  en: 'en_US',
  fr: 'fr_FR',
  da: 'da_DK',
  de: 'de_DE',
};

export function getLangLocale(lang: string): string {
  return langLocales[lang] || 'en_US';
}

function pick(value: { en: string; fr: string; da: string; de: string }, lang: string): string {
  if (lang === 'fr') return value.fr;
  if (lang === 'da') return value.da;
  if (lang === 'de') return value.de;
  return value.en;
}

export function buildAbsoluteUrl(path: string): string {
  if (!SITE_URL) return '';
  if (path === '/' || path === '') return SITE_URL;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function resolveSeoData(pathname: string, lang: string): SeoData {
  const cleanPath = (pathname || '/').replace(/\/+$/, '') || '/';

  if (cleanPath === '/') {
    return {
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      keywords: HOME_KEYWORDS,
      canonicalPath: '/',
      ogType: 'website',
      ogImage: HOME_OG_IMAGE,
      indexable: true,
    };
  }

  let slug = '';
  if (cleanPath.startsWith('/article/')) {
    slug = cleanPath.slice('/article/'.length);
  } else {
    slug = pathToSlug[cleanPath.replace(/^\//, '')] || '';
  }

  const article = articles.find((a) => a.slug === slug);
  const content = articleContent[slug];

  if (article && content) {
    const title = pick(
      { en: content.titleEn, fr: content.titleFr, da: content.titleDa, de: content.titleDe },
      lang,
    );
    const description = pick(
      {
        en: content.metaDescriptionEn,
        fr: content.metaDescriptionFr,
        da: content.metaDescriptionDa,
        de: content.metaDescriptionDe,
      },
      lang,
    );
    return {
      title,
      description,
      keywords: FALLBACK_KEYWORDS,
      canonicalPath: cleanPath,
      ogType: 'article',
      ogImage: article.image,
      indexable: true,
    };
  }

  // Unknown route (e.g. 404 catch-all) — not indexable.
  return {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    keywords: FALLBACK_KEYWORDS,
    canonicalPath: cleanPath,
    ogType: 'website',
    ogImage: HOME_OG_IMAGE,
    indexable: false,
  };
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertRobots(content: string | null) {
  let el = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (content === null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function applySeoHead(seo: SeoData, lang: string) {
  document.title = seo.title;
  upsertMeta('name', 'description', seo.description);
  upsertMeta('name', 'keywords', seo.keywords);

  const canonicalUrl = buildAbsoluteUrl(seo.canonicalPath);
  if (canonicalUrl) {
    upsertLink('canonical', canonicalUrl);
  }

  upsertRobots(seo.indexable ? null : 'noindex, nofollow');

  // Open Graph
  upsertMeta('property', 'og:type', seo.ogType);
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:title', seo.title);
  upsertMeta('property', 'og:description', seo.description);
  if (canonicalUrl) {
    upsertMeta('property', 'og:url', canonicalUrl);
  }
  upsertMeta('property', 'og:image', seo.ogImage);
  upsertMeta('property', 'og:locale', getLangLocale(lang));

  // Twitter Card
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', seo.title);
  upsertMeta('name', 'twitter:description', seo.description);
  upsertMeta('name', 'twitter:image', seo.ogImage);
}